import { Injectable } from '@angular/core';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import * as Sentry from '@sentry/angular';
import { finalize } from 'rxjs/operators';
import { SurveyFormComponent } from '../../../components/survey-form/survey-form.component';
import { SurveyFormFields } from '../../../core/models/survey-form/form-fields';
import { InvestmentService } from '../../../core/services/api/investment.service';
import { PrismicService } from '../../../core/services/api/prismic.service';
import { SurveyService } from '../../../core/services/api/survey.service';
import { ErrorService } from '../../../core/services/error.service';
import { ToastService } from '../../../core/services/toast.service';
import { BlockUiService } from '../blockUi/block-ui.service';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class InvestmentCancellationService {

  constructor(
    private modalService: NgbModal,
    private prismicService: PrismicService,
    private surveyService: SurveyService,
    private investmentService: InvestmentService,
    private blockUiService: BlockUiService,
    private errorService: ErrorService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) { }

  initiateCancellation(
    surveyType: 'refund' | 'cancel',
    investmentId: number,
    investorEmail: string,
    campaignName: string,
    campaignSlug: string,
    onSuccess: () => void
  ) {
    const popupOptions = {
      size: 'md',
      centered: true,
      keyboard: false,
      backdrop: 'static',
    } as NgbModalOptions;

    let surveyForm: SurveyFormFields;
    this.blockUiService.open();

    const surveyObservable =
      surveyType === 'refund'
        ? this.prismicService.getRefundInvestmentSurvey()
        : this.prismicService.getCancelInvestmentSurvey();

    surveyObservable.pipe(finalize(() => this.blockUiService.close())).subscribe({
      next: (data: SurveyFormFields) => {
        surveyForm = data;

        const modalRef = this.modalService.open(SurveyFormComponent, popupOptions);
        modalRef.componentInstance.surveyFormFields = surveyForm;

        modalRef.result.then(
          (formData) => {
            this.submitSurvey(investmentId, formData, surveyType, investorEmail, campaignName, campaignSlug, onSuccess);
          },
          () => {},
        );
      },
      error: (error) => {
        this.errorService.showError(error);
      },
    });
  }

  private submitSurvey(
    investmentId: number,
    surveyFormData: { reason: string; otherReason: string },
    surveyType: 'refund' | 'cancel',
    investorEmail: string,
    campaignName: string,
    campaignSlug: string,
    onSuccess: () => void
  ) {
    const submitObservable =
      surveyType === 'refund'
        ? this.surveyService.updateRefundInvestmentSurvey(
            moment(new Date()).format('DD/MM/YYYY hh:mm a'),
            investorEmail,
            campaignName,
            environment.baseUrl + '/equity/' + campaignSlug,
            surveyFormData.reason,
            surveyFormData.otherReason,
          )
        : this.surveyService.updateCancelInvestmentSurvey(
            moment(new Date()).format('DD/MM/YYYY hh:mm a'),
            investorEmail,
            campaignName,
            environment.baseUrl + '/equity/' + campaignSlug,
            surveyFormData.reason,
            surveyFormData.otherReason,
          );

    submitObservable.subscribe({
      next: () => {
        this.proceedWithCancellation(investmentId, surveyType, onSuccess);
      },
      error: (error) => {
        // If survey submission fails, still proceed with cancellation
        // Log the error but don't block the refund process
        Sentry.captureException(error, {
            tags: { context: 'survey_submission' },
            extra: { surveyType, investmentId },
        });
        this.proceedWithCancellation(investmentId, surveyType, onSuccess);
      },
    });
  }

  private proceedWithCancellation(
    investmentId: number,
    surveyType: 'refund' | 'cancel',
    onSuccess: () => void
  ) {
    this.blockUiService.open();
    this.investmentService
      .cancelInvestment(investmentId)
      .pipe(finalize(() => this.blockUiService.close()))
      .subscribe({
        next: () => {
          const messageKey =
            surveyType === 'refund'
              ? 'investment.requestRefundInProgress'
              : 'investment.cancelPledgeInProgress';

          this.toastService.success(this.translateService.instant(messageKey));
          onSuccess();
        },
        error: (err) => {
          // Cancellation/refund request failed. Log the error and show a failure message.
          Sentry.captureException(err, {
            tags: { context: 'investment_cancellation' },
            extra: { surveyType, investmentId },
          });

          const failureMessageKey =
            surveyType === 'refund'
              ? 'investment.requestRefundFailed'
              : 'investment.cancelPledgeFailed';

          this.toastService.error(this.translateService.instant(failureMessageKey));
        },
      });
  }
}
